import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'
import { Button } from './ui/button'
import { 
  MessageSquare, 
  Mail, 
  Share2, 
  Copy, 
  Check,
  Facebook,
  Instagram,
  X
} from 'lucide-react'

export function ShareModal({ open, onOpenChange }) {
  const [copied, setCopied] = useState(false)
  const currentUrl = typeof window !== 'undefined' ? window.location.href.split('?')[0] : ''
  const shareText = 'Check out PitchMetric - AI-Powered Sports Intelligence Platform'
  const shareTitle = 'PitchMetric - AI-Powered Sports Intelligence'

  const handleCopyLink = async () => {
    // Track Google Analytics event
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'share', {
        event_category: 'engagement',
        event_label: 'copy_link',
        method: 'copy_link'
      })
    }

    try {
      await navigator.clipboard.writeText(currentUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleShare = async (platform) => {
    const encodedUrl = encodeURIComponent(currentUrl)
    const encodedText = encodeURIComponent(shareText)
    const encodedTitle = encodeURIComponent(shareTitle)

    let shareUrl = ''

    switch (platform) {
      case 'sms':
        shareUrl = `sms:?body=${encodedText}%20${encodedUrl}`
        break
      case 'email':
        shareUrl = `mailto:?subject=${encodedTitle}&body=${encodedText}%0A%0A${encodedUrl}`
        break
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodedText}%20${encodedUrl}`
        break
      case 'twitter':
        shareUrl = `https://x.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`
        break
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`
        break
      case 'reddit':
        shareUrl = `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`
        break
      case 'instagram':
        // Instagram doesn't support direct URL sharing, so we'll copy the link
        // Track Google Analytics event for Instagram
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'share', {
            event_category: 'engagement',
            event_label: 'instagram',
            method: 'instagram'
          })
        }
        try {
          await navigator.clipboard.writeText(currentUrl)
          setCopied(true)
          setTimeout(() => setCopied(false), 2000)
        } catch (err) {
          console.error('Failed to copy:', err)
        }
        return
      case 'native':
        if (navigator.share) {
          try {
            await navigator.share({
              title: shareTitle,
              text: shareText,
              url: currentUrl,
            })
            // Track native share success
            if (typeof window !== 'undefined' && window.gtag) {
              window.gtag('event', 'share', {
                event_category: 'engagement',
                event_label: 'native_share',
                method: 'native_share'
              })
            }
            return
          } catch (err) {
            if (err.name !== 'AbortError') {
              console.error('Error sharing:', err)
            }
            return
          }
        }
        return
      default:
        return
    }

    // Track Google Analytics event for share platforms (before opening)
    if (shareUrl && typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'share', {
        event_category: 'engagement',
        event_label: platform,
        method: platform
      })
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'noopener,noreferrer')
    }
  }

  const shareOptions = [
    {
      id: 'native',
      name: 'More',
      icon: Share2,
      color: 'text-white',
      bgColor: 'bg-white/10 hover:bg-white/20',
      show: typeof navigator !== 'undefined' && navigator.share,
    },
    {
      id: 'sms',
      name: 'SMS',
      icon: MessageSquare,
      color: 'text-white',
      bgColor: 'bg-white/10 hover:bg-white/20',
      show: true,
    },
    {
      id: 'email',
      name: 'Email',
      icon: Mail,
      color: 'text-white',
      bgColor: 'bg-white/10 hover:bg-white/20',
      show: true,
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp',
      icon: MessageSquare,
      color: 'text-white',
      bgColor: 'bg-white/10 hover:bg-white/20',
      show: true,
    },
    {
      id: 'twitter',
      name: 'X',
      icon: X,
      color: 'text-white',
      bgColor: 'bg-white/10 hover:bg-white/20',
      show: true,
    },
    {
      id: 'facebook',
      name: 'Facebook',
      icon: Facebook,
      color: 'text-white',
      bgColor: 'bg-white/10 hover:bg-white/20',
      show: true,
    },
    {
      id: 'reddit',
      name: 'Reddit',
      icon: null, // Custom SVG icon
      color: 'text-white',
      bgColor: 'bg-white/10 hover:bg-white/20',
      show: true,
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: Instagram,
      color: 'text-white',
      bgColor: 'bg-white/10 hover:bg-white/20',
      show: true,
    },
  ].filter(option => option.show)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-black border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading font-bold mb-2">
            Share PitchMetric
          </DialogTitle>
          <DialogDescription className="text-gray-400 leading-relaxed">
            The more people who join early access, the faster we can finish building PitchMetric. 
            Share with friends and colleagues who need cutting-edge sports intelligence—help us launch sooner 
            and unlock exclusive insights together.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-6 space-y-4">
          {/* Share Options Grid */}
          <div className="grid grid-cols-4 gap-3">
            {shareOptions.map((option) => {
              const Icon = option.icon
              return (
                <button
                  key={option.id}
                  onClick={() => handleShare(option.id)}
                  className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl ${option.bgColor} transition-all active:scale-95`}
                >
                  {option.id === 'reddit' ? (
                    <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
                    </svg>
                  ) : (
                    <Icon className={`w-6 h-6 ${option.color}`} />
                  )}
                  <span className="text-xs text-gray-300">{option.name}</span>
                </button>
              )
            })}
          </div>

          {/* Copy Link Button */}
          <div className="pt-4 border-t border-white/10">
            <Button
              onClick={handleCopyLink}
              className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/10 h-12"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Link Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Link
                </>
              )}
            </Button>
          </div>

          {/* URL Display */}
          <div className="pt-2">
            <div className="bg-white/5 border border-white/10 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Link</p>
              <p className="text-sm text-gray-300 break-all">{currentUrl}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

